'use client'

import { ButtonArrow } from '@/components/ui/button-arrow'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { motion } from 'framer-motion'
import React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Cpu, QrCode, BookOpen, FileText, Search, Landmark } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

type FeatureCard = {
  id: string
  title: MessageDescriptor
  description: MessageDescriptor
  icon: LucideIcon
  iconClassName: string
  surfaceClassName: string
}

const featureCards: FeatureCard[] = [
  {
    id: 'qr-zone-management',
    title: msg`QR Zone Management`,
    description: msg`Identify and manage each plot with a unique QR code, integrated with digital maps.`,
    icon: QrCode,
    iconClassName: 'text-emerald-600',
    surfaceClassName: 'bg-emerald-50'
  },
  {
    id: 'digital-farm-log',
    title: msg`Digital Farm Log`,
    description: msg`Digitize fertilization, irrigation, and care processes. Data is timestamped and immutable.`,
    icon: BookOpen,
    iconClassName: 'text-sky-600',
    surfaceClassName: 'bg-sky-50'
  },
  {
    id: 'ai-iot-solution',
    title: msg`AI & IoT Solution`,
    description: msg`AI cameras for automatic quality grading and IoT sensors monitoring growing zones 24/7.`,
    icon: Cpu,
    iconClassName: 'text-amber-600',
    surfaceClassName: 'bg-amber-50'
  },
  {
    id: 'export-profile',
    title: msg`Export Profile`,
    description: msg`Auto-compile traceability dossiers meeting strict international market standards.`,
    icon: FileText,
    iconClassName: 'text-violet-600',
    surfaceClassName: 'bg-violet-50'
  },
  {
    id: 'buyer-traceability',
    title: msg`Buyer Traceability`,
    description: msg`Buyers can look up the full transparent journey of a product, immutably recorded on Blockchain.`,
    icon: Search,
    iconClassName: 'text-teal-600',
    surfaceClassName: 'bg-teal-50'
  },
  {
    id: 'agricultural-asset-digitization',
    title: msg`Agricultural Asset Digitization`,
    description: msg`Build digital asset profiles ready for digital finance models and carbon credits.`,
    icon: Landmark,
    iconClassName: 'text-rose-600',
    surfaceClassName: 'bg-rose-50'
  }
]

export default function FeaturedProjectSection() {
  const { i18n } = useLingui()

  const renderFeatureCard = (feature: FeatureCard, compact = false) => {
    const Icon = feature.icon

    return (
      <div
        key={feature.id}
        className={`group relative flex ${compact ? 'min-h-[164px] p-3.5' : 'h-[164px] w-64 p-4'} flex-col gap-2.5 overflow-hidden rounded-lg bg-[linear-gradient(145deg,#ffffff_0%,#f8fffd_52%,#eefbf7_100%)] text-slate-800 shadow-[0_14px_36px_rgba(15,23,42,0.07)] ring-1 ring-white/90 transition duration-300 before:absolute before:inset-x-4 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#0abce7]/45 before:to-transparent hover:-translate-y-1 hover:border-[#0abce7]/45 hover:shadow-[0_22px_48px_rgba(15,23,42,0.11)]`}
      >
        <div
          className={`flex ${compact ? 'size-9' : 'size-11'} items-center justify-center rounded-lg ${feature.surfaceClassName} ${feature.iconClassName} shadow-sm ring-1 ring-slate-900/5 transition duration-300 group-hover:scale-105 group-hover:shadow-md`}
        >
          <Icon className={compact ? 'size-[18px]' : 'size-5'} />
        </div>
        <p
          className={`${compact ? 'text-[11px]' : 'text-[13px]'} font-semibold leading-tight text-slate-950`}
        >
          {i18n._(feature.title)}
        </p>
        <p className={`${compact ? 'text-[10.5px]' : 'text-[11.5px]'} leading-snug text-slate-600`}>
          {i18n._(feature.description)}
        </p>
      </div>
    )
  }

  return (
    <section className='relative bg-white md:h-screen md:overflow-hidden min-h-screen py-10 lg:py-14 flex flex-col justify-start transition-colors'>
      <div className='container mx-auto px-4 flex flex-col items-center z-10'>
        {/* Section Header */}
        <motion.div
          className='text-center container max-w-3xl flex flex-col items-center gap-3'
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className='text-3xl md:text-5xl font-semibold leading-normal bg-text-gradient text-wrap whitespace-normal py-1'>
            {i18n._(msg`Smart Agriculture Traceability & IoT`)}
          </h2>
          <p className='font-jakarta text-slate-900 text-base md:text-lg lg:text-xl font-light leading-relaxed max-w-2xl'>
            {i18n._(
              msg`Pione Trace ensures transparent traceability, smart farming management, and real-time environmental monitoring by combining Blockchain, AI, and IoT sensors.`
            )}
          </p>

          <div className='pt-2'>
            <Link href='https://pionetrace.com' target='_blank' rel='noopener noreferrer'>
              <ButtonArrow showSvg className='font-normal'>
                {i18n._(msg`Discover more`)}
              </ButtonArrow>
            </Link>
          </div>
        </motion.div>
      </div>

      <Image
        src='/landing/images/bg-section-line.png'
        width={1900}
        height={231}
        alt='Background Section Line'
        className='object-contain w-full select-none pointer-events-none'
      />
      {/* Visual Showcase (Desktop-only) */}
      {/* max-w-[1100px] lg:max-w-[1200px] separates cards wider to both sides, h-[460px] extends space */}
      <div className='hidden md:block absolute bottom-[-20px] left-1/2 -translate-x-1/2 w-full max-w-[1200px] lg:max-w-[1300px] h-[540px] pointer-events-none z-20'>
        {/* Central Phone Mockup (relative inside this wrapper) */}
        <motion.div
          className='absolute bottom-[-2%] left-1/2 -translate-x-1/2 w-full max-w-[290px] h-[390px] flex items-center justify-center pointer-events-auto'
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className='absolute bottom-[-360px] left-1/2 h-[720px] w-[720px] -translate-x-1/2 rounded-full bg-[#0abce7]/20 pointer-events-none' />

          <Image
            src='/landing/home/pione-trace-app-iot-nong-nghiep-thong-minh copy.png'
            alt='Pione Trace App IoT'
            width={290}
            height={599}
            className='object-cover w-full z-10 h-auto hover:scale-[1.01] transition-all duration-500 rounded-t-2xl'
            priority
          />
        </motion.div>

        {/* === LEFT COLUMN: 3 cards evenly spaced === */}
        <motion.div
          className='absolute -top-20 bottom-20 left-[0%] lg:left-[5%] flex flex-col gap-5 justify-between py-4 pointer-events-auto'
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          {featureCards.slice(0, 3).map((feature) => renderFeatureCard(feature))}
        </motion.div>

        {/* === RIGHT COLUMN: 3 cards evenly spaced === */}
        <motion.div
          className='absolute -top-20 bottom-20 right-[0%] lg:right-[5%] flex flex-col gap-5 justify-between py-4 pointer-events-auto'
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          {featureCards.slice(3).map((feature) => renderFeatureCard(feature))}
        </motion.div>
      </div>

      {/* Mobile / Tablet Showcase (Visible only on mobile/tablet) */}
      <div className='flex flex-col items-center md:hidden mt-4 px-4 z-30 relative w-full'>
        {/* 6 Feature Cards in Grid – image is hidden on mobile */}
        <motion.div
          className='grid grid-cols-2 gap-3 w-full max-w-lg mt-6 '
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {featureCards.map((feature) => renderFeatureCard(feature, true))}
        </motion.div>
      </div>
    </section>
  )
}
